/**
 * TelefoniEditor — i numeri di una persona della Rete, quanti ne servono (24/10/2026).
 *
 * Prima c'era un campo solo, e chi ha un fisso e un cellulare ne perdeva uno. Qui ogni numero è una riga con la sua
 * etichetta libera («cellulare», «fisso»): si aggiunge, si toglie, e si sceglie quale è il principale (il primo, che
 * usano i bottoni in cima alla scheda). Il componente modifica soltanto la bozza: si salva col bottone della scheda,
 * insieme agli altri campi, e la pulizia (doppioni, righe vuote) la fa il servizio con `telefoniPuliti`.
 *
 * I contatti sono volutamente ridondanti: si perdono uno alla volta, non tutti insieme.
 */

import React from 'react';
import { Box, Button, IconButton, Link, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Delete, Phone, Star, StarBorder, WhatsApp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { numeroPerCollegamento } from '@shared/rete/telefoniRete';
import type { TelefonoRete } from '../../types/reteContatti';

interface Props {
  telefoni: TelefonoRete[];
  onChange: (telefoni: TelefonoRete[]) => void;
}

export const TelefoniEditor: React.FC<Props> = ({ telefoni, onChange }) => {
  const { t } = useTranslation('rete');

  const cambia = (indice: number, voce: Partial<TelefonoRete>) =>
    onChange(telefoni.map((x, i) => (i === indice ? { ...x, ...voce } : x)));
  const togli = (indice: number) => onChange(telefoni.filter((_, i) => i !== indice));
  const rendiPrincipale = (indice: number) =>
    onChange([telefoni[indice], ...telefoni.filter((_, i) => i !== indice)]);
  const aggiungi = () => onChange([...telefoni, { numero: '' }]);

  return (
    <Stack spacing={1}>
      <Typography variant="caption" sx={{ color: tokens.color.ash }}>{t('telefoni.titolo')}</Typography>
      {telefoni.length === 0 && (
        <Typography variant="body2" sx={{ color: tokens.color.mist, fontStyle: 'italic' }}>
          {t('telefoni.nessuno')}
        </Typography>
      )}
      {telefoni.map((voce, indice) => {
        const perLink = numeroPerCollegamento(voce.numero);
        return (
          // L'indice come chiave è voluto: le righe non hanno un id, e un numero si può scrivere uguale a un altro
          // finché non si salva.
          <Stack key={indice} direction="row" spacing={1} alignItems="center">
            <TextField
              label={t('telefoni.numero')}
              value={voce.numero}
              onChange={(e) => cambia(indice, { numero: e.target.value })}
              size="small"
              inputProps={{ inputMode: 'tel' }}
              sx={{ flex: 3, minWidth: 0 }}
            />
            <TextField
              label={t('telefoni.etichetta')}
              placeholder={t('telefoni.etichetta_esempio')}
              value={voce.etichetta ?? ''}
              onChange={(e) => cambia(indice, { etichetta: e.target.value })}
              size="small"
              sx={{ flex: 2, minWidth: 0 }}
            />
            {indice === 0 ? (
              <Tooltip title={t('telefoni.principale')}>
                {/* Stesso ingombro del bottone delle altre righe, così le colonne restano allineate. */}
                <Box sx={{ display: 'inline-flex', p: '5px' }}>
                  <Star fontSize="small" sx={{ color: tokens.color.ash }} />
                </Box>
              </Tooltip>
            ) : (
              <Tooltip title={t('telefoni.rendi_principale')}>
                <IconButton size="small" onClick={() => rendiPrincipale(indice)}>
                  <StarBorder fontSize="small" sx={{ color: tokens.color.mist }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('telefoni.chiama')}>
              <span>
                <IconButton size="small" component={Link} href={`tel:${perLink}`} disabled={!perLink}>
                  <Phone fontSize="small" sx={{ color: tokens.color.ash }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('telefoni.whatsapp')}>
              <span>
                <IconButton
                  size="small" component={Link} target="_blank" rel="noopener"
                  href={`https://wa.me/${perLink.replace(/^\+/, '')}`} disabled={!perLink}
                >
                  <WhatsApp fontSize="small" sx={{ color: tokens.color.ash }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('telefoni.togli')}>
              <IconButton size="small" onClick={() => togli(indice)}>
                <Delete fontSize="small" sx={{ color: tokens.color.danger }} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      })}
      <Button
        size="small" startIcon={<Add />} onClick={aggiungi}
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
      >
        {t('telefoni.aggiungi')}
      </Button>
    </Stack>
  );
};
